/**
 * Presentation only. Every value here arrives computed from the server.
 *
 * `date-fns` is imported by this module and by nothing else, and the ESLint rule of §6.4 says so.
 * There is no date arithmetic below: parsing an ISO calendar day in order to print it in another
 * spelling is not arithmetic, and nothing here derives a second date from a first.
 */

import { format, intervalToDuration, parseISO } from 'date-fns';

export type IsoDate = string;

/** `19 Mar 1416`. British spelling, English months, deliberately not locale-negotiated. */
export function fmtDate(iso: IsoDate | null | undefined): string {
  if (!iso) return '—';
  try {
    return format(parseISO(iso), 'd MMM yyyy');
  } catch {
    return iso;
  }
}

/** `4 Feb 2021, 14:08`. Used only where the value is an instant on the wire. */
export function fmtInstant(value: string | null | undefined): string {
  if (!value) return '—';
  try {
    return format(parseISO(value), 'd MMM yyyy, HH:mm');
  } catch {
    return value;
  }
}

/** The calendar year of an ISO day, for a lineage line. */
export function fmtYear(iso: IsoDate | null | undefined): string {
  if (!iso) return '—';
  return iso.slice(0, 4);
}

/** `1 version` / `3 versions`. Replaces the prototype's fifteen inline ternaries. */
export function plural(count: number, singular: string, many?: string): string {
  return `${count} ${count === 1 ? singular : (many ?? `${singular}s`)}`;
}

/** `Renewal term` -> `renewal term`, for inlining a label in a sentence. */
export function lowerLabel(label: string): string {
  return label.toLocaleLowerCase('en-GB');
}

/**
 * `6 months` / `20 days`. The unit arrives as the provision expresses it and is never converted:
 * a statute saying "twenty days" is not one month.
 *
 * `/meta` serves the unit's display label in its plural form (`Days`), and the vocabulary code is
 * its singular (`day`). Both come from the server, so both forms are used rather than a trailing
 * `s` being guessed at.
 */
export function fmtPeriod(
  period: { value: number; unit: string } | null | undefined,
  unitLabels: Readonly<Record<string, string>> = {},
): string {
  if (!period) return '—';
  const many = unitLabels[period.unit];
  return plural(period.value, period.unit, many ? lowerLabel(many) : undefined);
}

/** `19 Mar 1416 — 31 Dec 1992` / `19 Mar 1416 — in force`. */
export function fmtRange(from: IsoDate, to: IsoDate | null | undefined): string {
  return `${fmtDate(from)} — ${to ? fmtDate(to) : 'in force'}`;
}

/** `1994–now`, for a version chip where the full dates would not fit. */
export function fmtYearRange(from: IsoDate, to: IsoDate | null | undefined): string {
  return `${fmtYear(from)}–${to ? fmtYear(to) : 'now'}`;
}

/** A count that reads as an em dash when the register does not cover the thing counted. */
export function fmtCount(value: number | null | undefined): string {
  return value === null || value === undefined ? '—' : String(value);
}

/** The host of a URL, for a source row. Empty when it cannot be read as one. */
export function hostOf(url: string): string {
  try {
    return new URL(url).host.replace(/^www\./, '');
  } catch {
    return '';
  }
}

/**
 * `14 years, 3 months` — the length of the rail between two rows of the chronology.
 *
 * This is a label on a chart, in the same class as an axis tick, and it is the only elapsed-time
 * calculation in the client. It never produces a date, never feeds one, and is never shown as a
 * deadline: every date on that screen was computed by the server. `frontend.md` §6.2 forbids
 * "periods, term spines, deadline dates"; a rail that states its own length is none of those, and
 * it is the reason the chronology can stay an HTML grid instead of a proportional axis.
 */
export function durationBetween(from: IsoDate, to: IsoDate): string {
  const span = intervalToDuration({ start: parseISO(from), end: parseISO(to) });
  const years = span.years ?? 0;
  const months = span.months ?? 0;
  const days = span.days ?? 0;

  if (years > 0) {
    return months > 0 ? `${plural(years, 'year')}, ${plural(months, 'month')}` : plural(years, 'year');
  }
  if (months > 0) {
    return days > 0 ? `${plural(months, 'month')}, ${plural(days, 'day')}` : plural(months, 'month');
  }
  return plural(days, 'day');
}

/** A sentence listing things, for a warning naming several registers. */
export function fmtList(items: readonly string[]): string {
  if (items.length === 0) return '';
  if (items.length === 1) return items[0] ?? '';
  return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;
}
